import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define screen types based on wireframe
export type Screen =
  | 'onboarding'
  | 'dashboard'
  | 'customerSelect'
  | 'visitActive'
  | 'evidenceCapture'
  | 'visitSummary'
  | 'reports'
  | 'contextSales'
  | 'profile'
  | 'interactions' // Customer interactions management
  | 'products' // Product catalog screen
  | 'Phone' // Call functionality screen
  | 'help' // Help & Support screen
  | 'auth'
  | 'navTest' // Dev-only screen
  | 'directusTest'; // Dev-only database test screen

interface NavigationState {
  currentScreen: Screen;
  previousScreen: Screen | null;
  navigationHistory: Screen[];
  isLoading: boolean;
  activeTab?: string; // Store active tab for screens with tabs
}

// ONBOARDING DISABLED: Default to dashboard instead of onboarding screen
// To re-enable onboarding: change currentScreen to 'onboarding' and navigationHistory to ['onboarding']
const initialState: NavigationState = {
  currentScreen: 'dashboard',
  previousScreen: null,
  navigationHistory: ['dashboard'],
  isLoading: false,
  activeTab: undefined,
};

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    navigateTo: (state, action: PayloadAction<Screen>) => {
      state.previousScreen = state.currentScreen;
      state.currentScreen = action.payload;
      
      // Add to history if not the same as current
      if (state.navigationHistory[state.navigationHistory.length - 1] !== action.payload) {
        state.navigationHistory.push(action.payload);
      }
    },

    navigateToWithTab: (state, action: PayloadAction<{screen: Screen, tab?: string}>) => {
      state.previousScreen = state.currentScreen;
      state.currentScreen = action.payload.screen;
      state.activeTab = action.payload.tab;
      
      // Add to history if not the same as current
      if (state.navigationHistory[state.navigationHistory.length - 1] !== action.payload.screen) {
        state.navigationHistory.push(action.payload.screen);
      }
    },

    setActiveTab: (state, action: PayloadAction<string | undefined>) => {
      state.activeTab = action.payload;
    },
    
    goBack: (state) => {
      if (state.previousScreen) {
        const current = state.currentScreen;
        state.currentScreen = state.previousScreen;
        state.previousScreen = null;
        
        // Remove current from history if it's the last item
        if (state.navigationHistory[state.navigationHistory.length - 1] === current) {
          state.navigationHistory.pop();
        }
      }
    },
    
    resetNavigation: (state) => {
      // ONBOARDING DISABLED: Reset to dashboard instead of onboarding
      state.currentScreen = 'dashboard';
      state.previousScreen = null;
      state.navigationHistory = ['dashboard'];
      state.activeTab = undefined;
    },
    
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

// Selectors
export const selectCurrentScreen = (state: { navigation: NavigationState }) => state.navigation.currentScreen;
export const selectPreviousScreen = (state: { navigation: NavigationState }) => state.navigation.previousScreen;
export const selectNavigationHistory = (state: { navigation: NavigationState }) => state.navigation.navigationHistory;
export const selectActiveTab = (state: { navigation: NavigationState }) => state.navigation.activeTab;
export const selectIsLoading = (state: { navigation: NavigationState }) => state.navigation.isLoading;

export const { navigateTo, navigateToWithTab, goBack, resetNavigation, setLoading, setActiveTab } = navigationSlice.actions;
export default navigationSlice.reducer;